import graphql from "babel-plugin-relay/macro";
import { useCallback, useMemo } from "react";
import { useFragment } from "react-relay";

import { VStack } from "@whoosh/design-system/src/components/primitive";

import { AppliedPunchPassesSection } from "./AppliedPunchPassesSection";
import { AvailablePunchPassesSection } from "./AvailablePunchPassesSection";
import type { AvailablePunchPassesSectionItem } from "./AvailablePunchPassesSection";
import { UnavailablePunchPassesSection } from "./UnavailablePunchPassesSection";
import {
  BookingPunchPassContent_Booking$key,
  BookingPunchPassContent_Booking$data,
} from "./__generated__/BookingPunchPassContent_Booking.graphql";
import {
  PunchPassListItem_PunchPass$key,
  PunchPassTemplateType,
} from "./__generated__/PunchPassListItem_PunchPass.graphql";

export type BookingPunchPassContentProps = {
  bookingRef: BookingPunchPassContent_Booking$key;
  appliedId: string | null;
  onAppliedChange: (punchPassId: string | null) => void;
};

type BookingPunchPassNode = NonNullable<
  NonNullable<
    BookingPunchPassContent_Booking$data["punchPassOptionsValid"]
  >[number]
>;

function getRefForAppliedId(
  booking: BookingPunchPassContent_Booking$data,
  id: string | null
): PunchPassListItem_PunchPass$key | null {
  if (!id) return null;
  const applied = booking.appliedPunchPass;
  if (applied?.id === id) return applied;
  const valid = booking.punchPassOptionsValid ?? [];
  const node = valid.find((n) => n?.id === id) as
    | BookingPunchPassNode
    | undefined;
  return node ?? null;
}

export const BookingPunchPassContent = ({
  bookingRef,
  appliedId,
  onAppliedChange,
}: BookingPunchPassContentProps) => {
  const booking = useFragment(BookingPunchPassContentFragment, bookingRef);
  const appliedRefs = useMemo((): PunchPassListItem_PunchPass$key[] => {
    const ref = getRefForAppliedId(booking, appliedId);
    return ref ? [ref] : [];
  }, [booking, appliedId]);

  const handleRemove = useCallback(
    (_id: string) => {
      onAppliedChange(null);
    },
    [onAppliedChange]
  );

  const handleApply = useCallback(
    (id: string, _punchPassType?: PunchPassTemplateType) => {
      onAppliedChange(id);
    },
    [onAppliedChange]
  );

  const validOptions = useMemo(
    () => booking.punchPassOptionsValid ?? [],
    [booking.punchPassOptionsValid]
  );

  const availableItems: AvailablePunchPassesSectionItem[] = useMemo(
    () =>
      validOptions
        .filter(
          (opt): opt is BookingPunchPassNode =>
            opt != null && opt.id !== appliedId
        )
        .map((opt) => ({
          ref: opt as PunchPassListItem_PunchPass$key,
          id: opt.id,
          hasSameTypeApplied: appliedId != null,
        })),
    [validOptions, appliedId]
  );

  const unavailableRefs: readonly PunchPassListItem_PunchPass$key[] = useMemo(
    () =>
      (booking.punchPassOptionsInvalid ??
        []) as unknown as PunchPassListItem_PunchPass$key[],
    [booking.punchPassOptionsInvalid]
  );

  return (
    <VStack gap={2} backgroundColor="surface-secondary">
      <AppliedPunchPassesSection
        punchPassRefs={appliedRefs}
        onRemove={handleRemove}
      />
      <AvailablePunchPassesSection
        items={availableItems}
        onApply={handleApply}
        onRemove={handleRemove}
      />
      <UnavailablePunchPassesSection punchPassRefs={unavailableRefs} />
    </VStack>
  );
};

const BookingPunchPassContentFragment = graphql`
  fragment BookingPunchPassContent_Booking on Booking {
    id
    ownerClient {
      ... on Member {
        fullName
      }
      ... on Customer {
        fullName
      }
    }
    ... on StandardBooking {
      appliedPunchPass {
        id
        ...PunchPassListItem_PunchPass
      }
      punchPassOptionsValid: punchPassOptions(valid: true) {
        id
        ...PunchPassListItem_PunchPass
      }
      punchPassOptionsInvalid: punchPassOptions(valid: false) {
        id
        ...PunchPassListItem_PunchPass
      }
    }
    ... on LessonBooking {
      appliedPunchPass {
        id
        ...PunchPassListItem_PunchPass
      }
      punchPassOptionsValid: punchPassOptions(valid: true) {
        id
        ...PunchPassListItem_PunchPass
      }
      punchPassOptionsInvalid: punchPassOptions(valid: false) {
        id
        ...PunchPassListItem_PunchPass
      }
    }
  }
`;
